import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, CheckCircle2, Loader2 } from "lucide-react";
import { SERVICES, EVENT_TYPES, GUEST_COUNTS, TEAM_SIZES } from "../data/content";
import { track } from "../lib/tracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-white/10 bg-navy/70 px-4 py-3 text-sm text-ivory placeholder:text-steel/50 outline-none transition-all duration-300 focus:border-gold/60 focus:ring-1 focus:ring-gold/40";

const Field = ({ label, required, children, testid }) => (
  <label className="block" data-testid={testid}>
    <span className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.2em] text-steel">
      {label} {required && <span className="text-gold">*</span>}
    </span>
    {children}
  </label>
);

const INITIAL = {
  full_name: "", mobile: "", event_type: "", event_date: "", city: "", venue: "",
  guest_count: "", service_required: "", team_size: "", additional: "",
  preferred_contact: "Call", consent: false, company: "",
};

export const EnquiryForm = ({ testidPrefix = "enquiry" }) => {
  const [params] = useSearchParams();
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const service = params.get("service");
    if (service) setForm((f) => ({ ...f, service_required: service }));
  }, [params]);

  const set = (k) => (e) => {
    if (!started) {
      setStarted(true);
      track("enquiry_started");
    }
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiry`, form);
      track("enquiry_submitted", { event_type: form.event_type, city: form.city });
      setDone(true);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Could not submit right now. Please call or WhatsApp us instead.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    const waText = encodeURIComponent(
      `Hello Celebrity Bouncers, I just submitted an enquiry.\nName: ${form.full_name}\nEvent Type: ${form.event_type}\nEvent Date: ${form.event_date || "-"}\nCity: ${form.city}\nService: ${form.service_required}`
    );
    return (
      <div data-testid="enquiry-success-panel" className="border border-gold/30 bg-navy/60 p-8 text-center sm:p-12">
        <CheckCircle2 size={44} className="mx-auto text-gold" />
        <h3 className="mt-5 font-serif text-2xl font-medium text-ivory sm:text-3xl">Thank you.</h3>
        <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-steel sm:text-base">
          Our coordination team will review your event requirements and contact you shortly.
        </p>
        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <a
            href={`https://wa.me/919052910159?text=${waText}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="enquiry-continue-whatsapp-btn"
            onClick={() => track("whatsapp_click", { placement: "enquiry_success" })}
            className="flex items-center gap-2 bg-gold px-7 py-3.5 text-[12px] font-bold uppercase tracking-[0.2em] text-obsidian transition-colors duration-300 hover:bg-gold-soft"
          >
            <MessageCircle size={16} /> Continue on WhatsApp
          </a>
          <button
            data-testid="enquiry-submit-another-btn"
            onClick={() => {
              setDone(false);
              setForm(INITIAL);
              setStarted(false);
            }}
            className="text-xs font-semibold uppercase tracking-[0.2em] text-steel transition-colors duration-300 hover:text-gold"
          >
            Submit Another Enquiry
          </button>
        </div>
      </div>
    );
  }

  return (
    <form data-testid={`${testidPrefix}-form`} onSubmit={submit} className="grid gap-5 sm:grid-cols-2">
      <Field label="Full Name" required testid={`${testidPrefix}-field-name`}>
        <input data-testid={`${testidPrefix}-name-input`} required className={inputCls} value={form.full_name} onChange={set("full_name")} placeholder="Your full name" />
      </Field>
      <Field label="Mobile Number" required testid={`${testidPrefix}-field-mobile`}>
        <input data-testid={`${testidPrefix}-mobile-input`} required type="tel" className={inputCls} value={form.mobile} onChange={set("mobile")} placeholder="+91" />
      </Field>
      <Field label="Event Type" required testid={`${testidPrefix}-field-event-type`}>
        <select data-testid={`${testidPrefix}-event-type-select`} required className={inputCls} value={form.event_type} onChange={set("event_type")}>
          <option value="">Select event type</option>
          {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </Field>
      <Field label="Event Date" testid={`${testidPrefix}-field-date`}>
        <input data-testid={`${testidPrefix}-date-input`} type="date" className={`${inputCls} [color-scheme:dark]`} value={form.event_date} onChange={set("event_date")} />
      </Field>
      <Field label="City" required testid={`${testidPrefix}-field-city`}>
        <input data-testid={`${testidPrefix}-city-input`} required className={inputCls} value={form.city} onChange={set("city")} placeholder="e.g. Vijayawada" />
      </Field>
      <Field label="Venue" testid={`${testidPrefix}-field-venue`}>
        <input data-testid={`${testidPrefix}-venue-input`} className={inputCls} value={form.venue} onChange={set("venue")} placeholder="Venue name, if decided" />
      </Field>
      <Field label="Expected Guest Count" testid={`${testidPrefix}-field-guests`}>
        <select data-testid={`${testidPrefix}-guests-select`} className={inputCls} value={form.guest_count} onChange={set("guest_count")}>
          <option value="">Select range</option>
          {GUEST_COUNTS.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </Field>
      <Field label="Service Required" required testid={`${testidPrefix}-field-service`}>
        <select data-testid={`${testidPrefix}-service-select`} required className={inputCls} value={form.service_required} onChange={set("service_required")}>
          <option value="">Select service</option>
          {SERVICES.map((s) => <option key={s.id} value={s.title}>{s.title}</option>)}
          <option value="Multiple / Not sure">Multiple / Not sure</option>
        </select>
      </Field>
      <Field label="Preferred Team Size" testid={`${testidPrefix}-field-team-size`}>
        <select data-testid={`${testidPrefix}-team-size-select`} className={inputCls} value={form.team_size} onChange={set("team_size")}>
          <option value="">If known</option>
          {TEAM_SIZES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </Field>
      <Field label="Preferred Contact Method" testid={`${testidPrefix}-field-contact-method`}>
        <select data-testid={`${testidPrefix}-contact-method-select`} className={inputCls} value={form.preferred_contact} onChange={set("preferred_contact")}>
          <option value="Call">Call</option>
          <option value="WhatsApp">WhatsApp</option>
        </select>
      </Field>
      <div className="sm:col-span-2">
        <Field label="Additional Requirements" testid={`${testidPrefix}-field-additional`}>
          <textarea data-testid={`${testidPrefix}-additional-textarea`} rows={4} className={inputCls} value={form.additional} onChange={set("additional")} placeholder="Guest profile, timings, dress code, or anything else we should know" />
        </Field>
      </div>
      <input
        type="text"
        name="company"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="hidden"
        value={form.company}
        onChange={set("company")}
      />
      <label className="flex items-start gap-3 sm:col-span-2" data-testid={`${testidPrefix}-consent-wrapper`}>
        <input
          data-testid={`${testidPrefix}-consent-checkbox`}
          type="checkbox"
          required
          checked={form.consent}
          onChange={set("consent")}
          className="mt-1 h-4 w-4 shrink-0 accent-[#C7A45B]"
        />
        <span className="text-xs leading-relaxed text-steel">
          I consent to Celebrity Bouncers contacting me about this enquiry by call or WhatsApp. My event
          details will be kept private and used only to respond to this enquiry.
        </span>
      </label>
      <div className="sm:col-span-2">
        <button
          data-testid={`${testidPrefix}-submit-btn`}
          type="submit"
          disabled={submitting}
          className="flex w-full items-center justify-center gap-2 bg-gold px-8 py-4 text-[13px] font-bold uppercase tracking-[0.22em] text-obsidian transition-all duration-300 hover:bg-gold-soft disabled:opacity-60 sm:w-auto"
        >
          {submitting && <Loader2 size={16} className="animate-spin" />}
          {submitting ? "Sending..." : "Request a Quotation"}
        </button>
      </div>
    </form>
  );
};
